import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageBody } from './PageHead';

/*
 * RecordPage: one record (a Project, an Opportunity, a Quick Win) as its own
 * page. The same parts a RecordModal takes, laid out for reading: a back
 * link, the title block, the story in the main column, a sticky "at a glance"
 * rail beside it, and any decision pinned to the bottom so it stays in reach
 * while the record is read.
 */

interface BackState { backLabel?: string; backTo?: string }

/* Back goes where the reader came from. A record opened from a link or a
 * notification has nowhere in this tab to go back to, so it goes to Projects. */
export const useRecordBack = (fallback = '/projects', fallbackLabel = 'Back to Projects') => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as BackState;
  const cameFromApp = Boolean(state.backLabel) && (window.history.state?.idx ?? 0) > 0;
  const back = () => {
    if (cameFromApp) {
      navigate(-1);
      return;
    }
    navigate(fallback);
  };
  return { back, label: cameFromApp ? state.backLabel as string : fallbackLabel };
};

export const RecordPage: React.FC<{
  backLabel: string;
  onBack: () => void;
  kicker?: React.ReactNode;
  badge?: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  actions?: React.ReactNode;
  aside?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
}> = ({ backLabel, onBack, kicker, badge, title, sub, actions, aside, footer, children }) => (
  <PageBody>
    <div className="mx-auto w-full max-w-[1160px]">
      <button
        type="button"
        onClick={onBack}
        className="-ml-2 mb-5 inline-flex min-h-[40px] items-center gap-2 rounded-act px-2 text-[13.5px] font-semibold text-ink-muted transition-colors hover:bg-canvas-inset hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
      >
        <ArrowLeft size={16} aria-hidden />
        {backLabel}
      </button>

      <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4 border-b border-line pb-6">
        <div className="min-w-0 max-w-[760px] flex-[1_1_480px]">
          {(kicker || badge) && <div className="mb-3 flex flex-wrap items-center gap-2.5">{kicker}{badge}</div>}
          <h1 className="m-0 text-[26px] font-bold leading-[33px] tracking-[-0.015em] text-ink [text-wrap:balance] sm:text-[32px] sm:leading-[40px]">{title}</h1>
          {sub && <p className="m-0 mt-3 max-w-[68ch] text-[15px] leading-[25px] text-ink-muted">{sub}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
      </header>

      <div className={`mt-6 grid gap-6 ${aside ? 'lg:grid-cols-[minmax(0,1fr)_340px]' : ''}`}>
        <main className="min-w-0 space-y-5">{children}</main>
        {aside && <aside className="min-w-0 space-y-4 lg:sticky lg:top-6 lg:self-start">{aside}</aside>}
      </div>

      {footer && (
        <div className="sticky bottom-0 z-10 mt-6 border-t border-line bg-white/95 px-1 py-4 backdrop-blur supports-[backdrop-filter]:bg-white/85">
          {footer}
        </div>
      )}
    </div>
  </PageBody>
);

/* A titled block in the "at a glance" rail. */
export const RailCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section aria-label={title} className="overflow-hidden rounded-panel border border-line bg-white">
    <h2 className="m-0 px-5 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-[0.12em] text-eyebrow">{title}</h2>
    {children}
  </section>
);
